"use client";

import { useEffect, useState } from "react";
import { useAuth } from "../providers/AuthProvider";

const DISMISS_KEY = "ios_install_dismissed";
const DISMISS_DAYS = 7;

/**
 * Detects iOS Safari (not standalone) and shows a dismissable install banner.
 * Only renders for authenticated users who haven't dismissed within the last 7 days.
 */
export function IOSInstallBanner() {
  const { authenticated } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!authenticated) return;

    // Only show on iOS Safari, not in standalone mode
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|Chrome/.test(ua);
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true;

    if (!isIOS || !isSafari || isStandalone) return;

    // Check dismiss cooldown
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
      if (daysSince < DISMISS_DAYS) return;
    }

    // Delay slightly so the user sees the app first
    const timer = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timer);
  }, [authenticated]);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 28, // above the footer
        left: 12,
        right: 12,
        zIndex: 1000,
        background: "var(--color-toast-bg)",
        color: "var(--color-toast-text)",
        borderRadius: 12,
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
        fontSize: "0.85rem",
        lineHeight: 1.4,
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>Install occum.net</div>
        <div style={{ color: "var(--color-text-faintest)" }}>
          Tap{" "}
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ verticalAlign: "middle", display: "inline-block", color: "#6cf" }}
          >
            <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
            <polyline points="16 6 12 2 8 6" />
            <line x1="12" y1="2" x2="12" y2="15" />
          </svg>{" "}
          then &ldquo;Add to Home Screen&rdquo;
        </div>
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          background: "none",
          border: "none",
          color: "var(--color-text-faint)",
          fontSize: "1.2rem",
          cursor: "pointer",
          padding: "4px 8px",
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        &times;
      </button>
    </div>
  );
}
